import React from 'react';
import { X } from 'lucide-react';
import { useGiftDetailQuery } from '../../../services/useGifticonQueryService';
import { useCancelGiftItemMutation, useResendGiftItemMutation } from '../../../services/useGifticonMutationService';
import { parseGiftError } from '../../../api/gifticon';
import { GiftCouponStatus, GiftHistoryDisplayStatus } from '../../../types/gifticon';
import {
    getHistoryStatusGuidance,
    getHistoryStatusTone,
    getLockedCancelDisplayStatusReason,
    getLockedResendDisplayStatusReason,
    HistoryStatusLabel,
    HistoryStatusTone,
    shouldKeepScheduledHistoryLabel,
    toHistoryStatusLabel,
    toLegacyDetailDisplayStatus,
} from '../historyStatus';

type HistoryDetail = {
    id: string;
    /** 서버 발송 건의 소속 batch — 상세 조회·재발송·취소 API에 필요 */
    batchId?: string;
    date: string;
    receiver: string;
    receiverType: '회원' | '강사' | '단체';
    reason: string;
    route: string;
    product: string;
    status: HistoryStatusLabel;
    displayStatus?: GiftHistoryDisplayStatus;
    payment: '충전 잔액' | '바로 결제';
    amount: number;
    trainerName?: string;
    reregStatus?: 'YES' | 'EXPIRED' | 'ACTIVE';
    reregCount?: number;
};

const point = (value: number) => `${value.toLocaleString()}P`;
const money = (value: number) => `${value.toLocaleString()}원`;

const STATUS_TONE_CLASSES: Record<HistoryStatusTone, string> = {
    blue: 'bg-[#ECF1FC] text-[#3D73DD] border-[#C3D4F4]',
    green: 'bg-[#E5F8F3] text-[#008C7B] border-[#B3E8DE]',
    orange: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]',
    gray: 'bg-[#F2F4F6] text-[#4A4A4A] border-[#E5E7EB]',
    red: 'bg-[#FEF2F2] text-[#DC2626] border-[#FCA5A5]',
};

const COUPON_STATUS_LABELS: Record<GiftCouponStatus, string> = {
    NOT_ISSUED: '미발급',
    PENDING: '발급 대기',
    SENT: '발송 완료 (미사용)',
    USED: '사용 완료',
    EXPIRED: '기간 만료',
    CANCELING: '취소 처리 중',
    CANCELED: '취소됨',
    FAILED: '발급 실패',
};

const HistoryDetailModal = ({
    history,
    accessToken,
    onClose,
}: {
    history: HistoryDetail;
    accessToken: string;
    onClose: () => void;
}) => {
    const batchId = history.batchId ?? '';
    const detailQuery = useGiftDetailQuery({ accessToken, batchId });
    const resendMutation = useResendGiftItemMutation(accessToken);
    const cancelMutation = useCancelGiftItemMutation(accessToken);
    // 서버 상세에서 이 행(itemId)에 해당하는 쿠폰만 골라 상태를 보여준다
    const detailItem = detailQuery.data?.items.find((item) => item.itemId === history.id);
    const isActionable = Boolean(batchId);
    const isPending = resendMutation.isLoading || cancelMutation.isLoading;
    const detailDisplayStatus = detailItem?.displayStatus
        ?? (detailItem ? toLegacyDetailDisplayStatus(detailItem.couponStatus) : undefined);
    const displayStatus = detailDisplayStatus ?? history.displayStatus;
    // 예약 목록을 연 뒤 스케줄러가 처리했다면 최신 상세 상태가 예약 라벨보다 우선한다.
    const isScheduled = shouldKeepScheduledHistoryLabel(history.status, detailDisplayStatus);
    const currentStatus = isScheduled
        ? history.status
        : toHistoryStatusLabel(displayStatus) ?? history.status;
    const statusGuidance = isScheduled
        ? '예약된 시각 전이라 아직 쿠폰을 발급하지 않았어요.'
        : getHistoryStatusGuidance(displayStatus);
    const scheduledLockedReason = isScheduled
        ? '예약 발송 전에는 쿠폰 재발송이나 개별 취소를 할 수 없어요.'
        : null;
    // 이미 사용/취소/만료된 쿠폰은 재발송·취소가 서버에서도 거부되므로 버튼을 미리 잠근다.
    // FAILED는 발급 자체가 안 돼 쿠폰번호가 없다 — resend(문자 재전송)가 404로 떨어지므로 함께 잠근다.
    const lockedCouponStatus: GiftCouponStatus | null =
        detailItem && (['NOT_ISSUED', 'USED', 'CANCELING', 'CANCELED', 'EXPIRED', 'FAILED'] as GiftCouponStatus[]).includes(detailItem.couponStatus)
            ? detailItem.couponStatus
            // 상세가 아직 안 왔어도 목록 상태가 '발급 실패'면 잠근다
            : history.status === '발급 실패' ? 'FAILED'
            : null;
    const lockedCouponReason = lockedCouponStatus === 'NOT_ISSUED' ? '발급되지 않은 건은 재발송하거나 취소할 수 없어요.'
        : lockedCouponStatus === 'USED' ? '사용 완료된 쿠폰은 재발송하거나 취소할 수 없어요.'
        : lockedCouponStatus === 'CANCELING' ? '쿠폰 취소 결과를 확인 중이에요.'
        : lockedCouponStatus === 'CANCELED' ? '취소된 쿠폰은 재발송하거나 다시 취소할 수 없어요.'
        : lockedCouponStatus === 'EXPIRED' ? '기간이 만료된 쿠폰은 재발송하거나 취소할 수 없어요.'
        : lockedCouponStatus === 'FAILED' ? '발급에 실패한 선물은 자동화 탭의 보류함에서 다시 보낼 수 있어요.'
        : null;
    const lockedResendReason = scheduledLockedReason
        ?? getLockedResendDisplayStatusReason(displayStatus)
        ?? lockedCouponReason;
    const lockedCancelReason = scheduledLockedReason
        ?? getLockedCancelDisplayStatusReason(displayStatus)
        ?? lockedCouponReason;

    const alertError = (error: unknown, fallback: string) => {
        const parsed = parseGiftError(error);
        window.alert(parsed?.message || fallback);
    };

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(15,23,42,0.38)] px-[24px]">
            <div className="w-full max-w-[480px] rounded-[8px] bg-white p-[24px] shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
                <div className="flex items-start justify-between gap-[16px]">
                    <div>
                        <div className="flex items-center gap-[9px]">
                            <h2 className="text-[18px] font-bold leading-[26px] text-[#1F2937]">발송 상세</h2>
                            <span className={`inline-flex h-[24px] items-center rounded-full border px-[9px] text-[12px] font-bold ${STATUS_TONE_CLASSES[getHistoryStatusTone(displayStatus, currentStatus)]}`}>
                                {currentStatus}
                            </span>
                        </div>
                        <p className="mt-[2px] text-[13px] text-[#64748B]">{history.date}</p>
                    </div>
                    <button type="button" aria-label="닫기" onClick={onClose} className="rounded-[8px] p-[6px] hover:bg-[#F8FAFC]">
                        <X size={20} color="#64748B" />
                    </button>
                </div>

                <div className="mt-[16px] space-y-[9px]">
                    {([
                        ['받는 사람', `${history.receiver} (${history.receiverType})`],
                        ...(detailItem?.phoneMasked ? [['연락처', detailItem.phoneMasked]] : []),
                        ...(history.trainerName ? [['담당 강사', history.trainerName]] : []),
                        ['상품', history.product],
                        ['발송 사유', history.reason],
                        ['발송 경로', history.route],
                        ['결제 방식', history.payment],
                        // 내역 목록 행과 동일 규칙 — 바로 결제는 원화, 충전 잔액은 포인트
                        ['금액', history.payment === '바로 결제' ? money(history.amount) : point(history.amount)],
                        ...(history.reregCount !== undefined
                            ? [['재등록', `${history.reregCount}명 재등록`]]
                            : history.reregStatus
                                ? [['재등록', history.reregStatus === 'YES' ? '재등록 완료' : history.reregStatus === 'EXPIRED' ? '만료' : '활성 (수강 중)']]
                                : []),
                    ] as [string, string][]).map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between border-b border-[#F1F5F9] pb-[9px]">
                            <span className="text-[13px] text-[#64748B]">{label}</span>
                            <b className="max-w-[280px] truncate text-[13px] text-[#111827]">{value}</b>
                        </div>
                    ))}
                </div>

                <div className="mt-[14px] rounded-[8px] bg-[#F8FAFC] p-[13px]">
                    <p className="text-[12px] font-bold text-[#64748B]">보낸 메시지 · 쿠폰 상태</p>
                    {statusGuidance && (
                        <p className="mt-[4px] text-[12px] leading-[18px] text-[#475569]">{statusGuidance}</p>
                    )}
                    {!isActionable ? (
                        <p className="mt-[4px] text-[12px] leading-[18px] text-[#94A3B8]">이 발송 건은 상세 정보를 조회할 수 없습니다.</p>
                    ) : detailQuery.isLoading ? (
                        <p className="mt-[4px] text-[12px] leading-[18px] text-[#94A3B8]">상세 정보를 불러오는 중입니다…</p>
                    ) : detailQuery.isError ? (
                        <p className="mt-[4px] text-[12px] leading-[18px] text-[#DC2626]">상세 정보를 불러오지 못했어요.</p>
                    ) : (
                        <>
                            <p className="mt-[4px] whitespace-pre-wrap text-[12px] leading-[18px] text-[#334155]">
                                {detailQuery.data?.message || '메시지 없이 발송되었습니다.'}
                            </p>
                            <p className="mt-[7px] text-[12px] font-bold text-[#475569]">
                                쿠폰 상태 · {detailItem ? COUPON_STATUS_LABELS[detailItem.couponStatus] : '확인할 수 없음'}
                                {detailItem?.usedAt ? ` · 사용 ${detailItem.usedAt.slice(0, 10)}` : ''}
                                {detailItem?.expiresAt ? ` · 만료 ${detailItem.expiresAt.slice(0, 10)}` : ''}
                            </p>
                        </>
                    )}
                </div>

                <div className="mt-[14px] flex gap-[8px]">
                    <button
                        type="button"
                        disabled={!isActionable || isPending || Boolean(lockedResendReason)}
                        onClick={() => {
                            resendMutation.mutate({ batchId, itemId: history.id }, {
                                onSuccess: () => window.alert('선물을 다시 발송했어요.'),
                                onError: (error) => alertError(error, '재발송에 실패했어요. 잠시 후 다시 시도해 주세요.'),
                            });
                        }}
                        className="h-[40px] flex-1 rounded-[8px] border border-[#CBD5E1] text-[12px] font-bold text-[#475569] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:border-[#E2E8F0] disabled:text-[#CBD5E1] disabled:hover:bg-white"
                    >
                        재발송
                    </button>
                    <button
                        type="button"
                        disabled={!isActionable || isPending || Boolean(lockedCancelReason)}
                        onClick={() => {
                            if (!window.confirm(`${history.receiver}님에게 보낸 선물을 취소할까요? 이미 사용된 쿠폰은 취소할 수 없어요.`)) return;
                            cancelMutation.mutate({ batchId, itemId: history.id }, {
                                onSuccess: () => {
                                    window.alert('발송을 취소했어요.');
                                    onClose();
                                },
                                onError: (error) => alertError(error, '발송 취소에 실패했어요. 잠시 후 다시 시도해 주세요.'),
                            });
                        }}
                        className="h-[40px] flex-1 rounded-[8px] border border-[#FCA5A5] text-[12px] font-bold text-[#DC2626] hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:border-[#E2E8F0] disabled:text-[#CBD5E1] disabled:hover:bg-white"
                    >
                        발송 취소
                    </button>
                </div>
                {!isActionable ? (
                    <p className="mt-[6px] text-center text-[11px] text-[#94A3B8]">서버에 연동되지 않은 발송 건은 재발송·취소할 수 없습니다.</p>
                ) : lockedResendReason || lockedCancelReason ? (
                    <div className="mt-[6px] space-y-[2px] text-center text-[11px] text-[#94A3B8]">
                        {lockedResendReason && <p>재발송 불가 · {lockedResendReason}</p>}
                        {lockedCancelReason && lockedCancelReason !== lockedResendReason && (
                            <p>취소 불가 · {lockedCancelReason}</p>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default HistoryDetailModal;
